import React, { useEffect, useState } from "react";
import {
    SiFastapi, SiDjango, SiExpress, SiSpringboot, SiMongodb, SiPostgresql, SiMysql,
    SiReact, SiVuedotjs, SiNextdotjs, SiAngular, SiPython, SiNodedotjs, SiOpenjdk,
    SiDotnet, SiFlutter
} from "react-icons/si";
import { API_RESPONSE } from "../../settings/shared/constants";
import StackService from "../../services/api/stackservice/index";
import "../../assets/css/stack/stack.css";

const LANGUAGES = [
    { label: "Python", value: "python", Icon: SiPython, color: "#3776ab" },
    { label: "Java", value: "java", Icon: SiOpenjdk, color: "#e76f00" },
    { label: "Node.js", value: "nodejs", Icon: SiNodedotjs, color: "#5fa04e" },
    { label: "C#", value: "csharp", Icon: SiDotnet, color: "#512bd4" },
    // { label: "Dart", value: "dart", Icon: SiFlutter, color: "#02569b" },
];

const BACKENDS = [
    { label: "FastAPI", value: "fastapi", language: "python", port: 8000, Icon: SiFastapi, color: "#009688" },
    { label: "Django", value: "django", language: "python", port: 8000, Icon: SiDjango, color: "#0c4b33" },
    { label: "Spring Boot", value: "springboot", language: "java", port: 8080, Icon: SiSpringboot, color: "#6db33f" },
    { label: "Express", value: "express", language: "nodejs", port: 3000, Icon: SiExpress, color: "#303030" },
    { label: "ASP.NET Core", value: "aspnetcore", language: "csharp", port: 5000, Icon: SiDotnet, color: "#512bd4" },
];

const DATABASES = [
    { label: "MongoDB", value: "mongodb", Icon: SiMongodb, color: "#47a248" },
    { label: "PostgreSQL", value: "postgresql", Icon: SiPostgresql, color: "#336791" },
    { label: "MySQL", value: "mysql", Icon: SiMysql, color: "#00758f" },
];

const FRONTENDS = [
    { label: "React + Vite", value: "react_vite", port: 5173, Icon: SiReact, color: "#149eca" },
    { label: "Vue + Vite", value: "vue_vite", port: 5173, Icon: SiVuedotjs, color: "#42b883" },
    { label: "Next.js", value: "nextjs", port: 3000, Icon: SiNextdotjs, color: "#111111" },
    { label: "Angular", value: "angular", port: 4200, Icon: SiAngular, color: "#dd0031" },
    { label: "Flutter", value: "flutter", port: 8080, Icon: SiFlutter, color: "#02569b" },
];

// Service name: starts with a letter, then letters / numbers / - / _ ; 2 to 30 characters
const NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9_-]{1,29}$/;
const NAME_HINT = "2-30 characters: letters, numbers, - or _ (start with a letter)";

const emptyBackend = () => ({ name: "", language: "", framework: "", port: "" });
const toPort = (v) => (v === "" || v === null ? null : Number(v));
const nextFreePort = (base, used) => { let p = base; while (used.includes(p)) p += 1; return p; };
const find = (list, value) => list.find((o) => o.value === value);

const Tile = ({ option, selected, disabled, onClick, small }) => (
    <button
        type="button"
        className={`stack-tile${selected ? " is-selected" : ""}${small ? " stack-tile--small" : ""}`}
        disabled={disabled}
        onClick={onClick}
        title={disabled ? "Already used in this stack" : option.label}
    >
        <span className="stack-tile__icon" style={{ color: option.color }}><option.Icon /></span>
        <span className="stack-tile__label">{option.label}</span>
    </button>
);

const Step = ({ n, title, hint, children }) => (
    <section className="stack-card">
        <header className="stack-card__head">
            <span className="stack-card__num">{n}</span>
            <div className="stack-card__titles">
                <h3>{title}</h3>
                {hint && <p>{hint}</p>}
            </div>
        </header>
        {children}
    </section>
);

const Node = ({ option, title, port, empty }) => {
    if (empty) return <div className="stack-node stack-node--empty">{title}</div>;
    return (
        <div className="stack-node">
            <span className="stack-node__icon" style={{ color: option.color }}><option.Icon /></span>
            <div className="stack-node__text">
                <strong>{option.label}</strong>
                {title && <small>{title}</small>}
            </div>
            {port ? <span className="stack-node__port">:{port}</span> : null}
        </div>
    );
};

export default function StackPage() {
    const [backends, setBackends] = useState([emptyBackend()]);
    const [engine, setEngine] = useState("");
    const [frontend, setFrontend] = useState({ framework: "", port: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [imageId, setImageId] = useState("");
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);
    const [confirmIndex, setConfirmIndex] = useState(null);   // which service shows the remove warning

    // Close the remove warning on outside click or Escape
    useEffect(() => {
        if (confirmIndex === null) return undefined;
        const onDown = (e) => { if (!e.target.closest(".stack-pop, .stack-link")) setConfirmIndex(null); };
        const onKey = (e) => { if (e.key === "Escape") setConfirmIndex(null); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [confirmIndex]);

    // Ports used by everything except backend `index` (-1 = check for the frontend)
    const portsUsedByOthers = (index) => [
        ...backends.filter((_, i) => i !== index).map((b) => toPort(b.port)),
        ...(index === -1 ? [] : [toPort(frontend.port)]),
    ].filter((p) => p !== null);

    const updateBackend = (i, patch) => setBackends((l) => l.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
    const onLanguage = (i, language) => updateBackend(i, { language, framework: "", port: "" });
    const onFramework = (i, value) => {
        const opt = find(BACKENDS, value);
        updateBackend(i, { framework: value, port: nextFreePort(opt.port, portsUsedByOthers(i)) });
    };
    const onFrontend = (value) => {
        const opt = find(FRONTENDS, value);
        setFrontend({ framework: value, port: nextFreePort(opt.port, portsUsedByOthers(-1)) });
    };
    const removeBackend = (i) => {
        setBackends((l) => l.filter((_, idx) => idx !== i));
        setConfirmIndex(null);
    };

    const nameError = (b) => (b.name.trim() && !NAME_REGEX.test(b.name.trim()) ? NAME_HINT : "");
    const portError = (b, i) =>
        toPort(b.port) !== null && portsUsedByOthers(i).includes(toPort(b.port))
            ? "Port already used in this stack" : "";
    const frontendPortError =
        toPort(frontend.port) !== null && portsUsedByOthers(-1).includes(toPort(frontend.port))
            ? "Port already used by a backend" : "";

    const hasErrors = backends.some((b, i) => nameError(b) || portError(b, i)) || frontendPortError;
    const backendsFilled = backends.every((b) => b.name.trim() && b.language && b.framework);
    const steps = [backendsFilled, !!engine, !!frontend.framework];
    const progress = Math.round((steps.filter(Boolean).length / steps.length) * 100);
    const canSubmit = steps.every(Boolean) && !hasErrors && !saving;

    const handleSubmit = async () => {
        setError(""); setSaving(true);
        try {
            const res = await StackService.createStack({
                backends: backends.map((b) => ({
                    name: b.name.trim(), language: b.language, framework: b.framework, port: toPort(b.port),
                })),
                database: { engine },
                frontend: { framework: frontend.framework, port: toPort(frontend.port) },
            });
            if (res?.status === API_RESPONSE.SUCCESS) { setSaved(true); setImageId(res.imageId || ""); }
            else {
                const msg = res.errorMessage || "Could not save the stack.";
                setError(res.imageId ? `${msg} (${res.imageId})` : msg);
            }
        } catch (e) {
            console.error("STACK SAVE ERROR:", e);
            setError("Could not save the stack. Please try again.");
        } finally { setSaving(false); }
    };

    const reset = () => {
        setBackends([emptyBackend()]); setEngine(""); setFrontend({ framework: "", port: "" });
        setSaved(false); setImageId(""); setError(""); setCopied(false); setConfirmIndex(null);
    };

    const copyId = async () => {
        try { await navigator.clipboard.writeText(imageId); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch (e) { /* clipboard unavailable */ }
    };

    const db = find(DATABASES, engine);
    const fe = find(FRONTENDS, frontend.framework);

    return (
        <div className="stack-page">
            <header className="stack-hero">
                <h2>Build your stack</h2>
                <p>Pick the pieces. We assemble the image and wire the ports for you.</p>
            </header>

            <div className="stack-layout">
                <div className="stack-form">
                    <Step n="1" title="Backend services" hint="Add one or more services. Each needs a valid name and a unique port.">
                        <div className="stack-services">
                            {backends.map((b, i) => (
                                <div className="stack-service" key={i}>
                                    <div className="stack-service__top">
                                        <span className="stack-service__tag">Service {i + 1}</span>
                                        {backends.length > 1 && (
                                            <div className="stack-service__actions">
                                                <button
                                                    type="button"
                                                    className="stack-link"
                                                    onClick={() => setConfirmIndex(confirmIndex === i ? null : i)}
                                                >
                                                    Remove
                                                </button>
                                                {confirmIndex === i && (
                                                    <div className="stack-pop" role="alertdialog" aria-label="Confirm remove">
                                                        <strong>Remove {b.name.trim() ? `"${b.name.trim()}"` : `Service ${i + 1}`}?</strong>
                                                        <p>Its name, framework and port will be lost.</p>
                                                        <div className="stack-pop__btns">
                                                            <button type="button" onClick={() => setConfirmIndex(null)}>Cancel</button>
                                                            <button type="button" className="is-danger" onClick={() => removeBackend(i)}>Remove</button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="stack-row">
                                        <label className={`stack-input${nameError(b) ? " is-invalid" : ""}`}>
                                            <span>Name</span>
                                            <input
                                                type="text"
                                                placeholder="e.g. api, worker"
                                                maxLength={30}
                                                value={b.name}
                                                onChange={(e) => updateBackend(i, { name: e.target.value })}
                                            />
                                        </label>
                                        <label className={`stack-input stack-input--port${portError(b, i) ? " is-invalid" : ""}`}>
                                            <span>Port</span>
                                            <input type="number" value={b.port} disabled={!b.framework}
                                                onChange={(e) => updateBackend(i, { port: e.target.value })} />
                                        </label>
                                    </div>
                                    {nameError(b) && <p className="stack-field-error">{nameError(b)}</p>}
                                    {portError(b, i) && <p className="stack-field-error">{portError(b, i)}</p>}

                                    <p className="stack-label">Language</p>
                                    <div className="stack-tiles">
                                        {LANGUAGES.map((l) => (
                                            <Tile key={l.value} option={l} small selected={b.language === l.value}
                                                onClick={() => onLanguage(i, l.value)} />
                                        ))}
                                    </div>
                                    {b.language && (
                                        <div className="stack-reveal">
                                            <p className="stack-label">Framework</p>
                                            <div className="stack-tiles">
                                                {BACKENDS.filter((o) => o.language === b.language).map((o) => (
                                                    <Tile key={o.value} option={o} selected={b.framework === o.value}
                                                        onClick={() => onFramework(i, o.value)} />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                            <button type="button" className="stack-add" onClick={() => setBackends((l) => [...l, emptyBackend()])}>
                                <span>+</span> Add another service
                            </button>
                        </div>
                    </Step>

                    <div className="stack-pair">
                        <Step n="2" title="Database" hint="Where your data lives.">
                            <div className="stack-tiles">
                                {DATABASES.map((d) => (
                                    <Tile key={d.value} option={d} selected={engine === d.value} onClick={() => setEngine(d.value)} />
                                ))}
                            </div>
                        </Step>

                        <Step n="3" title="Frontend" hint="The interface your users see.">
                            <div className="stack-tiles">
                                {FRONTENDS.map((f) => (
                                    <Tile key={f.value} option={f} selected={frontend.framework === f.value} onClick={() => onFrontend(f.value)} />
                                ))}
                            </div>
                            {frontend.framework && (
                                <div className="stack-reveal">
                                    <label className={`stack-input stack-input--port${frontendPortError ? " is-invalid" : ""}`}>
                                        <span>Port</span>
                                        <input type="number" value={frontend.port}
                                            onChange={(e) => setFrontend({ ...frontend, port: e.target.value })} />
                                    </label>
                                    {frontendPortError && <p className="stack-field-error">{frontendPortError}</p>}
                                </div>
                            )}
                        </Step>
                    </div>
                </div>

                <aside className="stack-side">
                    {saved ? (
                        <div className="stack-done">
                            <div className="stack-done__check">✓</div>
                            <h3>Stack created</h3>
                            {imageId && (
                                <>
                                    <p className="stack-label">Image ID</p>
                                    <div className="stack-idbox">
                                        <code>{imageId}</code>
                                        <button type="button" onClick={copyId}>{copied ? "Copied" : "Copy"}</button>
                                    </div>
                                </>
                            )}
                            <button type="button" className="stack-btn stack-btn--ghost" onClick={reset}>Create another</button>
                        </div>
                    ) : (
                        <div className="stack-preview">
                            <div className="stack-preview__head">
                                <h3>Your architecture</h3>
                                <span>{progress}%</span>
                            </div>
                            <div className="stack-bar"><div style={{ width: `${progress}%` }} /></div>

                            <div className="stack-flow">
                                {fe ? <Node option={fe} title="Frontend" port={frontend.port} /> : <Node empty title="Choose a frontend" />}
                                <div className="stack-link-line" />
                                <div className="stack-flow__group">
                                    {backends.map((b, i) => {
                                        const opt = find(BACKENDS, b.framework);
                                        return opt
                                            ? <Node key={i} option={opt} title={b.name.trim() || `Service ${i + 1}`} port={b.port} />
                                            : <Node key={i} empty title={`Backend ${i + 1}: choose a framework`} />;
                                    })}
                                </div>
                                <div className="stack-link-line" />
                                {db ? <Node option={db} title="Database" /> : <Node empty title="Choose a database" />}
                            </div>

                            {error && <p className="stack-error">{error}</p>}
                            <button type="button" className="stack-btn" onClick={handleSubmit} disabled={!canSubmit}>
                                {saving ? "Saving..." : "Save stack"}
                            </button>
                            {!canSubmit && !saving && (
                                <p className="stack-note">
                                    {hasErrors ? "Fix the highlighted fields to continue." : "Complete all three steps to save."}
                                </p>
                            )}
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
}